import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import Paper from "@mui/material/Paper";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import SpeedIcon from "@mui/icons-material/Speed";
import { useTranslation } from "react-i18next";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import PlayerFooterButton from "core/components/PlayerFooterButton";

const PREFIX = "PlaybackSpeedButton";

const classes = {
  root: `${PREFIX}-root`,
  paper: `${PREFIX}-paper`,
  speedLabel: `${PREFIX}-speedLabel`,
  listItem: `${PREFIX}-listItem`,
};

const Root = styled("div")(({ theme }) => ({
  position: "relative",
  height: 36,

  [theme.breakpoints.up("sm")]: {
    height: 44,
  },
  [theme.breakpoints.up("md")]: {
    height: 48,
  },

  [`& .${classes.paper}`]: {
    backgroundColor: "rgba(0,0,0,0.75)",
    position: "absolute",
    bottom: 60,
    right: -12,
    overflow: "hidden",
    minWidth: 80,
  },
  [`& .${classes.speedLabel}`]: {
    fontSize: "0.7rem",
    color: "white",
    position: "absolute",
    top: "2px",
    right: "2px",
    backgroundColor: "rgba(255,255,255,0.2)",
    borderRadius: "4px",
    padding: "1px 3px",
    lineHeight: 1,
  },
  [`& .${classes.listItem}`]: {
    justifyContent: "center",
    color: "white",
    fontSize: "0.9rem",
    minHeight: 36,
    "&.Mui-selected": {
      backgroundColor: "rgba(255,255,255,0.2)",
    },
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.1)",
    },
  },
}));

const PLAYBACK_SPEEDS = [0.5, 1.0, 1.25, 1.5, 2.0];

function PlaybackSpeedButton({ videoActions, videoState }) {
  const { t } = useTranslation("core");
  const [open, setOpen] = useState(false);

  const handleClick = () => {
    setOpen((v) => !v);
  };

  const handleClickAway = () => {
    setOpen(false);
  };

  const handleClickSpeed = (speed) => () => {
    videoActions.setPlaybackRate(speed);
    setOpen(false);
  };

  if (!videoState || !videoActions) return null;

  const currentSpeed = videoState.playbackRate || 1.0;
  const showSpeedLabel = currentSpeed !== 1.0;

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Root>
        <PlayerFooterButton
          onClick={handleClick}
          title={
            open
              ? ""
              : t("playbackSpeedButton.title", "Скорость воспроизведения")
          }
        >
          <SpeedIcon />
          {showSpeedLabel && (
            <div className={classes.speedLabel}>{currentSpeed}x</div>
          )}
        </PlayerFooterButton>

        {open && (
          <Paper className={classes.paper}>
            <List disablePadding dense>
              {PLAYBACK_SPEEDS.map((speed) => (
                <ListItem
                  key={speed}
                  button
                  selected={Math.abs(currentSpeed - speed) < 0.01}
                  onClick={handleClickSpeed(speed)}
                  className={classes.listItem}
                >
                  {speed}x
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
      </Root>
    </ClickAwayListener>
  );
}

PlaybackSpeedButton.propTypes = {
  videoState: PropTypes.object,
  videoActions: PropTypes.object,
};

export default PlaybackSpeedButton;
